import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@marine/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@marine/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@marine/ui/popover';
import { Badge } from '@marine/ui/badge';
import { formatCurrency } from '@marine/utils/formatters';
import { useEngines } from '@/modules/inventory/hooks/useEngines';
import { useTrailers } from '@/modules/inventory/hooks/useTrailers';
import type { Engine } from '@/modules/inventory/types/engine.types';
import type { Trailer } from '@/modules/inventory/types/trailer.types';

type AssetItem = Engine | Trailer;

interface AssetItemComboboxProps {
  type: 'engine' | 'trailer';
  value?: string | null;
  onChange: (itemId: string | null) => void;
  className?: string;
  placeholder?: string;
}

/**
 * SP4 slice 3 — engine/trailer picker for the quote form, mirroring
 * InventoryBoatCombobox over the SP2-A asset list APIs (terminal statuses
 * are already default-hidden by the backend, so sold stock never surfaces).
 */
export function AssetItemCombobox({
  type,
  value,
  onChange,
  className,
  placeholder = 'Search inventory...',
}: AssetItemComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 250);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  const filters = useMemo(() => {
    const next: Record<string, any> = { per_page: 25 };
    if (debouncedQuery) next.search = debouncedQuery;
    return next;
  }, [debouncedQuery]);

  const enginesQuery = useEngines(filters, { enabled: type === 'engine' });
  const trailersQuery = useTrailers(filters, { enabled: type === 'trailer' });
  const query = type === 'engine' ? enginesQuery : trailersQuery;
  const items: AssetItem[] = query.data?.items ?? [];
  const isLoadingItems = query.isLoading || query.isFetching;

  const itemName = (item: AssetItem) =>
    'engine_manufacturer' in item && item.engine_manufacturer != null
      ? [item.engine_manufacturer, (item as Engine).engine_model].filter(Boolean).join(' ')
      : [(item as Trailer).trailer_make, (item as Trailer).trailer_model].filter(Boolean).join(' ') ||
        item.inventory_number ||
        (type === 'engine' ? 'Engine' : 'Trailer');

  const selected = items.find((item) => item.id === value);

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between">
            {selected ? (
              <div className="flex items-center gap-2 truncate">
                <span className="truncate">{itemName(selected)}</span>
                {selected.inventory_number && (
                  <Badge variant="outline" className="ml-2 shrink-0 text-xs font-mono">
                    {selected.inventory_number}
                  </Badge>
                )}
              </div>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[500px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={`Search by make, model, stock#${type === 'trailer' ? ', or VIN' : ', or serial'}...`}
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {!isLoadingItems && (
                <CommandEmpty>
                  <div className="text-center py-6">
                    <Search className="mx-auto h-8 w-8 text-muted-foreground/50 mb-2" />
                    <p className="text-sm text-muted-foreground">No {type}s found in inventory</p>
                  </div>
                </CommandEmpty>
              )}
              <CommandGroup>
                {isLoadingItems ? (
                  <div className="py-6 text-center text-sm">Loading inventory...</div>
                ) : (
                  items.map((item) => (
                    <CommandItem
                      key={item.id}
                      value={item.id}
                      onSelect={() => {
                        onChange(item.id === value ? null : item.id);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn('mr-2 h-4 w-4 shrink-0', value === item.id ? 'opacity-100' : 'opacity-0')}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{itemName(item)}</span>
                          {item.inventory_number && (
                            <Badge variant="outline" className="text-xs font-mono">
                              {item.inventory_number}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">
                          {item.condition || 'Condition not specified'}
                          {item.our_price != null
                            ? ` • ${formatCurrency(Number(item.our_price), {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}`
                            : ''}
                        </div>
                      </div>
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
